import { z } from 'zod';

/**
 * SIL-1155: comparable-FMV display DTO (customer + ops portals).
 *
 * The comparable FMV is an INFORMATIONAL range borrowed from equivalence-group
 * siblings' comps (SIL-1153, comparable_fmv_estimate). It is never the asset's
 * FMV. This DTO is range-only by construction: there is no point-estimate field,
 * no raw confidence score, and no suppression detail. A suppressed estimate has
 * no DTO at all - the field is OMITTED from the response, not null.
 */
export const comparableFmvConfidenceSchema = z.enum(['low', 'medium', 'high']);
export type ComparableFmvConfidence = z.infer<typeof comparableFmvConfidenceSchema>;

export const comparableFmvSchema = z.object({
  assetId: z.string().uuid(),
  low: z.number(),
  high: z.number(),
  confidence: comparableFmvConfidenceSchema,
  computedAt: z.string(),
});
export type ComparableFmv = z.infer<typeof comparableFmvSchema>;

/**
 * Score -> label. comparable_fmv_estimate.confidence_score is capped at 0.6
 * (borrowed comps are never as trustworthy as a matched SKU's own), so the
 * bands are relative to that cap: < 0.3 low, < 0.5 medium, else high. The raw
 * score is never sent to a client.
 */
export function comparableFmvConfidenceLabel(score: number): ComparableFmvConfidence {
  if (score < 0.3) return 'low';
  if (score < 0.5) return 'medium';
  return 'high';
}
